import { useState, useMemo } from 'react';
import {
  ChevronDown, ChevronUp, LayoutGrid, List, Pencil, Plus, Settings, Trash2, Upload, X
} from 'lucide-react';
import { useData, useApp } from '../context.jsx';

const PRIORITIES = ['', 'High', 'Medium', 'Low'];
const PRIORITY_CLASS = { High: 'priority-high', Medium: 'priority-medium', Low: 'priority-low' };
const PRIORITY_ORDER = { High: 0, Medium: 1, Low: 2, '': 3 };

// ─── CSV parsing ──────────────────────────────────────────────────────────────

const FIELD_MAP = {
  title:    ['title', 'subject', 'name', 'task', 'taskname'],
  dueDate:  ['duedate', 'due', 'deadline', 'date'],
  priority: ['priority'],
  status:   ['status', 'state'],
  assignee: ['assignee', 'assignedto', 'owner', 'responsible'],
  notes:    ['notes', 'description', 'details', 'note', 'comments']
};

function normalizeHeader(h) {
  return h.toLowerCase().replace(/[\s_\-]+/g, '');
}

function parseCsvLine(line) {
  const result = [];
  let cur = '';
  let inQ = false;
  for (const ch of line) {
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ',' && !inQ) { result.push(cur); cur = ''; }
    else { cur += ch; }
  }
  result.push(cur);
  return result;
}

function parseCsv(text, projectStatuses) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const rawHeaders = parseCsvLine(lines[0]).map(normalizeHeader);
  const colMap = rawHeaders.map((h) => {
    for (const [field, aliases] of Object.entries(FIELD_MAP)) {
      if (aliases.includes(h)) return field;
    }
    return null;
  });
  return lines.slice(1).map((line) => {
    const vals = parseCsvLine(line);
    const task = { id: crypto.randomUUID(), title: '', dueDate: '', priority: '', status: '', assignee: '', notes: '' };
    colMap.forEach((field, i) => {
      if (!field) return;
      const val = (vals[i] || '').trim().replace(/^"|"$/g, '');
      if (field === 'priority') {
        const norm = val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
        task.priority = PRIORITIES.includes(norm) ? norm : '';
      } else if (field === 'status') {
        const match = projectStatuses.find((s) => s.toLowerCase() === val.toLowerCase());
        task.status = match || projectStatuses[0] || '';
      } else {
        task[field] = val;
      }
    });
    if (!task.status) task.status = projectStatuses[0] || '';
    return task;
  }).filter((t) => t.title);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${m}/${day}/${y}`;
}

// ─── Task Modal ───────────────────────────────────────────────────────────────

function TaskModal({ project, task, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({ ...task });
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-card crm-modal">
        <div className="modal-header">
          <h3>{task.id ? 'Edit task' : 'New task'}</h3>
          <button className="icon-button" onClick={onClose} type="button"><X size={18} /></button>
        </div>
        <div className="crm-modal-body">
          <label className="crm-field-label">
            Title
            <input
              className="crm-field-input"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Task title"
              autoFocus
            />
          </label>
          <div className="crm-field-row">
            <label className="crm-field-label">
              Status
              <select className="crm-field-input" value={form.status} onChange={(e) => set('status', e.target.value)}>
                {project.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="crm-field-label">
              Priority
              <select className="crm-field-input" value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p || 'None'}</option>)}
              </select>
            </label>
          </div>
          <div className="crm-field-row">
            <label className="crm-field-label">
              Due date
              <input className="crm-field-input" type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
            </label>
            <label className="crm-field-label">
              Assignee
              <input className="crm-field-input" value={form.assignee} onChange={(e) => set('assignee', e.target.value)} placeholder="Name" />
            </label>
          </div>
          <label className="crm-field-label">
            Notes
            <textarea
              className="crm-field-input crm-notes"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={4}
              placeholder="Additional notes…"
            />
          </label>
        </div>
        <div className="modal-footer">
          {task.id && (
            <button className="ghost-button danger-button" onClick={() => onDelete(task.id)} type="button">
              <Trash2 size={15} /> Delete
            </button>
          )}
          <div className="modal-footer-right">
            <button className="ghost-button" onClick={onClose} type="button">Cancel</button>
            <button
              className="primary-button"
              onClick={() => form.title.trim() && onSave(form)}
              disabled={!form.title.trim()}
              type="button"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Status Editor ────────────────────────────────────────────────────────────

function StatusEditor({ statuses, onChange, onClose }) {
  const [list, setList] = useState([...statuses]);
  const [draft, setDraft] = useState('');

  const add = () => {
    const val = draft.trim();
    if (!val || list.includes(val)) return;
    setList([...list, val]);
    setDraft('');
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-card">
        <div className="modal-header">
          <h3>Manage statuses</h3>
          <button className="icon-button" onClick={onClose} type="button"><X size={18} /></button>
        </div>
        <div className="status-editor-list">
          {list.map((s) => (
            <div key={s} className="status-editor-row">
              <span>{s}</span>
              <button
                className="icon-button"
                onClick={() => setList(list.filter((x) => x !== s))}
                disabled={list.length <= 1}
                type="button"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="status-editor-add">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="New status…"
            className="crm-field-input"
          />
          <button className="ghost-button" onClick={add} type="button"><Plus size={14} /> Add</button>
        </div>
        <div className="modal-footer">
          <div className="modal-footer-right">
            <button className="ghost-button" onClick={onClose} type="button">Cancel</button>
            <button className="primary-button" onClick={() => onChange(list)} type="button">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Task Card ────────────────────────────────────────────────────────────────

function TaskCard({ task, onClick, isDragging, onDragStart, onDragEnd }) {
  const dragged = { current: false };
  return (
    <div
      className={`task-card${isDragging ? ' dragging' : ''}`}
      draggable
      onDragStart={(e) => { dragged.current = true; onDragStart(e); }}
      onDragEnd={(e) => { onDragEnd(e); setTimeout(() => { dragged.current = false; }, 0); }}
      onClick={() => { if (!dragged.current) onClick(); }}
    >
      <p className="task-card-title">{task.title}</p>
      <div className="task-card-meta">
        {task.dueDate && <span className="task-meta-item">{fmtDate(task.dueDate)}</span>}
        {task.priority && (
          <span className={`priority-chip ${PRIORITY_CLASS[task.priority] || ''}`}>{task.priority}</span>
        )}
        {task.assignee && <span className="task-meta-item task-meta-assignee">{task.assignee}</span>}
      </div>
    </div>
  );
}

// ─── Kanban Board ─────────────────────────────────────────────────────────────

function KanbanBoard({ project, onTaskClick, onAddTask, onMoveTask }) {
  const [dragOverStatus, setDragOverStatus] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const startDrag = (e, task) => {
    e.dataTransfer.setData('taskId', task.id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(task.id);
  };

  const endDrag = () => {
    setDraggingId(null);
    setDragOverStatus(null);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStatus !== status) setDragOverStatus(status);
  };

  const handleDrop = (e, status) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    setDragOverStatus(null);
    setDraggingId(null);
    if (taskId) onMoveTask(taskId, status);
  };

  return (
    <div className="kanban-board">
      {project.statuses.map((status) => {
        const tasks = project.tasks.filter((t) => t.status === status);
        return (
          <div
            key={status}
            className={`kanban-column${dragOverStatus === status ? ' drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, status)}
            onDrop={(e) => handleDrop(e, status)}
          >
            <div className="kanban-column-header">
              <span className="kanban-status-name">{status}</span>
              <span className="kanban-count">{tasks.length}</span>
              <button className="icon-button" onClick={() => onAddTask(status)} title="Add task" type="button">
                <Plus size={14} />
              </button>
            </div>
            <div className="kanban-column-body">
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onClick={() => onTaskClick(task)}
                  isDragging={draggingId === task.id}
                  onDragStart={(e) => startDrag(e, task)}
                  onDragEnd={endDrag}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────

const LIST_COLS = [
  { field: 'title', label: 'Title' },
  { field: 'status', label: 'Status' },
  { field: 'priority', label: 'Priority' },
  { field: 'dueDate', label: 'Due Date' },
  { field: 'assignee', label: 'Assignee' }
];

function TaskListView({ project, onTaskClick, sort, onSort }) {
  const sorted = useMemo(() => {
    return [...project.tasks].sort((a, b) => {
      if (sort.field === 'priority') {
        const av = PRIORITY_ORDER[a.priority] ?? 3;
        const bv = PRIORITY_ORDER[b.priority] ?? 3;
        return sort.dir === 'asc' ? av - bv : bv - av;
      }
      const av = a[sort.field] || '';
      const bv = b[sort.field] || '';
      return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [project.tasks, sort.field, sort.dir]);

  return (
    <div className="task-list-wrap">
      <table className="task-table">
        <thead>
          <tr>
            {LIST_COLS.map(({ field, label }) => (
              <th key={field} className="task-table-th" onClick={() => onSort(field)}>
                {label}
                {sort.field === field && (
                  sort.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((task) => (
            <tr key={task.id} className="task-table-row" onClick={() => onTaskClick(task)}>
              <td className="task-table-title">{task.title}</td>
              <td><span className="status-badge">{task.status}</span></td>
              <td>
                {task.priority && (
                  <span className={`priority-chip ${PRIORITY_CLASS[task.priority] || ''}`}>{task.priority}</span>
                )}
              </td>
              <td>{fmtDate(task.dueDate)}</td>
              <td>{task.assignee || '—'}</td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={5} className="task-table-empty">No tasks yet — create one or import a CSV.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function Crm() {
  const { data, updateData } = useData();
  const { activeCrmProject } = useApp();

  const [view, setView] = useState('board');
  const [taskModal, setTaskModal] = useState(null);
  const [showStatusEditor, setShowStatusEditor] = useState(false);
  const [sort, setSort] = useState({ field: 'dueDate', dir: 'asc' });

  const project = data.crm?.projects?.find((p) => p.id === activeCrmProject);

  if (!project) {
    return (
      <div className="crm-empty">
        <p>Select a project from the sidebar, or click <strong>New project</strong> to get started.</p>
      </div>
    );
  }

  const updateProject = (recipe) => {
    updateData((current) => ({
      ...current,
      crm: {
        ...current.crm,
        projects: current.crm.projects.map((p) => (p.id === project.id ? recipe(p) : p))
      }
    }));
  };

  const openNewTask = (defaultStatus = project.statuses[0] || '') => {
    setTaskModal({ id: '', title: '', dueDate: '', priority: '', status: defaultStatus, assignee: '', notes: '' });
  };

  const handleSaveTask = (form) => {
    updateProject((p) => ({
      ...p,
      tasks: form.id
        ? p.tasks.map((t) => (t.id === form.id ? form : t))
        : [...p.tasks, { ...form, id: crypto.randomUUID() }]
    }));
    setTaskModal(null);
  };

  const handleMoveTask = (taskId, newStatus) => {
    updateProject((p) => ({
      ...p,
      tasks: p.tasks.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    }));
  };

  const handleDeleteTask = (id) => {
    if (!window.confirm('Delete this task?')) return;
    updateProject((p) => ({ ...p, tasks: p.tasks.filter((t) => t.id !== id) }));
    setTaskModal(null);
  };

  const handleSaveStatuses = (statuses) => {
    updateProject((p) => ({ ...p, statuses }));
    setShowStatusEditor(false);
  };

  const handleSort = (field) => {
    setSort((s) => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const tasks = parseCsv(ev.target.result, project.statuses);
      if (tasks.length === 0) {
        window.alert('No tasks found. Make sure your CSV has a "Title" or "Subject" column.');
        return;
      }
      if (!window.confirm(`Import ${tasks.length} task${tasks.length !== 1 ? 's' : ''}?`)) return;
      updateProject((p) => ({ ...p, tasks: [...p.tasks, ...tasks] }));
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleRenameProject = () => {
    const name = window.prompt('Project name:', project.name);
    if (name?.trim()) updateProject((p) => ({ ...p, name: name.trim() }));
  };

  return (
    <div className="crm-view">
      <div className="crm-toolbar">
        <div className="crm-toolbar-left">
          <button className="icon-button" onClick={handleRenameProject} title="Rename project" type="button">
            <Pencil size={15} />
          </button>
          <span className="crm-task-count">
            {project.tasks.length} task{project.tasks.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="crm-toolbar-right">
          <button className="ghost-button" onClick={() => setShowStatusEditor(true)} type="button">
            <Settings size={15} /> Statuses
          </button>
          <label className="ghost-button" title="Import CSV">
            <Upload size={15} /> Import CSV
            <input type="file" accept=".csv" onChange={handleCsvImport} />
          </label>
          <div className="view-toggle">
            <button
              className={`view-toggle-btn${view === 'board' ? ' active' : ''}`}
              onClick={() => setView('board')}
              title="Board view"
              type="button"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`view-toggle-btn${view === 'list' ? ' active' : ''}`}
              onClick={() => setView('list')}
              title="List view"
              type="button"
            >
              <List size={16} />
            </button>
          </div>
          <button className="primary-button" onClick={() => openNewTask()} type="button">
            <Plus size={15} /> Create Task
          </button>
        </div>
      </div>

      {view === 'board'
        ? <KanbanBoard project={project} onTaskClick={setTaskModal} onAddTask={openNewTask} onMoveTask={handleMoveTask} />
        : <TaskListView project={project} onTaskClick={setTaskModal} sort={sort} onSort={handleSort} />
      }

      {taskModal && (
        <TaskModal
          project={project}
          task={taskModal}
          onSave={handleSaveTask}
          onDelete={handleDeleteTask}
          onClose={() => setTaskModal(null)}
        />
      )}

      {showStatusEditor && (
        <StatusEditor
          statuses={project.statuses}
          onChange={handleSaveStatuses}
          onClose={() => setShowStatusEditor(false)}
        />
      )}
    </div>
  );
}
